import React, { useEffect, useState } from "react";
import { GdanskSaleForm } from "../../../models/GdanskSaleForm";
import { get } from "../../../api";
import { GET_LEGACY_EXERCISES } from "../../../api/apiRoutes";
import useFacility from "../../../../utils/hooks/useFacility";
import { GDANSK_PASS } from "../Steps/gdanskSaleStepsConsts";
import { format } from "date-fns";
import EASkeletonLoader from "../../shared/EASkeletonLoader";
import { GdanskLaneExercises } from "../../../models/GdanskLaneExercises";
import { ExerciseInt } from "../../../models/ExerciseInt";
import GdanskLaneExerciseTile from "./GdanskLaneExerciseTile";
import getId from "../../../../utils/getId";

interface IProps {
  form: GdanskSaleForm;
  setForm: (form: GdanskSaleForm) => void;
  isInstructor?: boolean;
}

const GdanskLaneSlotLegend: React.FC = () => (
  <div className="gdansk-lane-slot-legend" role="region" aria-label="Legenda dostępności slotów">
    <div className="gdansk-lane-slot-legend__item">
      <span className="gdansk-lane-slot-legend__swatch gdansk-lane-slot-legend__swatch--available" aria-hidden />
      <p className="no-line-height">Miejsca dostępne w sprzedaży internetowej.</p>
    </div>
    <div className="gdansk-lane-slot-legend__item">
      <span className="gdansk-lane-slot-legend__swatch gdansk-lane-slot-legend__swatch--full" aria-hidden />
      <p className="no-line-height">
        Pula biletów przeznaczonych do sprzedaży internetowej została wyczerpana. W celu sprawdzenia dostępności miejsc
        w kasie pływalni zapraszamy do kontaktu telefonicznego.
      </p>
    </div>
  </div>
);

const GdanskLaneSelectInput: React.FC<IProps> = ({ form, setForm, isInstructor = false }) => {
  const facilityIds = useFacility();
  const [loading, setLoading] = useState(false);
  const [exercises, setExercises] = useState<GdanskLaneExercises>({} as GdanskLaneExercises);

  const getExercises = async () => {
    setLoading(true);
    const dateFromAndDateTo = format(form.date, "yyyy-MM-dd");
    const exerciseGroupIds = isInstructor ? "1,114" : ""; // DODATKOWY PARAMETR SZYTWNIAK, ABY ZWRACAĆ TOR DLA INTRUKTORA
    const { result, isError } = await get(
      GET_LEGACY_EXERCISES(
        facilityIds,
        form.productMainType === GDANSK_PASS,
        dateFromAndDateTo,
        dateFromAndDateTo,
        exerciseGroupIds,
      ),
    );
    if (!isError) {
      // Sortowanie kluczy (godzin) w obiekcie result
      const sortedResult = Object.keys(result)
        .sort((timeA, timeB) => {
          const [startA] = timeA.split("-");
          const [startB] = timeB.split("-");
          return new Date(`1970-01-01T${startA}:00`).getTime() - new Date(`1970-01-01T${startB}:00`).getTime();
        })
        .reduce(
          (acc, key) => {
            acc[key] = result[key]; // Tworzymy posortowany obiekt
            return acc;
          },
          {} as typeof result,
        );

      setExercises(sortedResult);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (form.date) {
      getExercises();
    }
    //eslint-disable-next-line
  }, [form.date, form.productMainType]);

  if (!form.date) {
    return null;
  }

  if (loading) {
    return <EASkeletonLoader height="300px" />;
  }

  if (Object.keys(exercises).length === 0) {
    return (
      <div className="alert alert-yellow">
        <p>Niestety, nie znaleźliśmy żadnych terminów dla podanego dnia.</p>
      </div>
    );
  }

  return (
    <div className="gdansk-lane-select">
      <table className="lane-table">
        <thead>
          <tr>
            <th>Godzina</th>
            {Object.keys(exercises).length > 0 &&
              Object.keys(exercises[Object.keys(exercises)[0]]).map((columnSlot) => (
                <th key={columnSlot}>{columnSlot}</th>
              ))}
          </tr>
        </thead>
        <tbody>
          {Object.keys(exercises).map((timeSlot) => (
            <tr key={timeSlot}>
              <td>{timeSlot}</td>
              {Object.keys(exercises[timeSlot]).map((columnSlot) => {
                const exercisesInSlot: ExerciseInt[] = exercises[timeSlot][columnSlot];

                return (
                  <td key={columnSlot}>
                    {exercisesInSlot.map((exercise) => (
                      <GdanskLaneExerciseTile
                        key={exercise.objectId}
                        exercise={exercise}
                        onSelect={(value) => setForm({ ...form, exercise: value })}
                        isSelected={Boolean(getId(form.exercise) && getId(form.exercise) === getId(exercise))}
                      />
                    ))}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <GdanskLaneSlotLegend />
    </div>
  );
};

export default GdanskLaneSelectInput;
